import { useCallback, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import downloadFile from '@framework/api/downloadFile';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { SessionService } from '@users/session/SessionService';
import UserPermission from '@users/user/UserPermission';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import DownloadRepository from '@/creditflowresearch/infrastructure/repository/DownloadRepository';
import type { TBondsFiltersPayload } from '@/creditflowresearch/application/Bonds/BondsFiltersContext';

export interface IExportBondsPayload {
  assetClassId?: string,
  search: string,
  filtersPayload: TBondsFiltersPayload,
}

export interface IUseExportBondsToExcel {
  isFetching: boolean,
  exportToExcel: (payload: IExportBondsPayload) => Promise<void>,
}

export const useExportBondsToExcel = (): IUseExportBondsToExcel => {
  const container = getContainer();
  const sessionService = container.get(SessionService);
  const downloadRepository = container.get(DownloadRepository);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const exportToExcel = useCallback(async ({ assetClassId, search, filtersPayload }: IExportBondsPayload) => {
    setIsFetching(true);
    try {
      // Logs the user out if their session is no longer valid. It never blocks the export.
      await sessionService.checkSession({
        accessPermissions: UserPermission.managerAccess,
        logoutParams: { redirect: false },
      });
      const exportUrl = await downloadRepository.getBondExcelExportUrl(
        assetClassId,
        filtersPayload,
        search,
        window.location.href,
      );
      downloadFile(exportUrl);
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    } finally {
      setIsFetching(false);
    }
  }, [sessionService, downloadRepository]);

  return { isFetching, exportToExcel };
};
